import React, { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useTheme } from '@mui/material';
import { VisitorProvider } from '../context/VisitorContext';
import { PageHeader } from '../components/VisitorList/PageHeader';
import { VisitorTable } from '../components/VisitorList/VisitorTable';
import type { Participant } from '../../../shared/services/visitors/visitorService';
import { useVisitor } from '../context';
import { useNavigate } from 'react-router-dom';
import { EventSelector } from '../components/EventSelector';
import { exportToPDF, exportToExcel } from '../../../shared/utils/exportUtils';
import { useToast } from '../../../shared/hooks/useToast';
import { visitorService } from '../../../shared/services/visitors/visitorService';

function VisitorListContent({ eventId, setEventId }: { eventId: string; setEventId: (id: string) => void }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    participants,
    eventTitle,
    page,
    limit,
    total,
    setPage,
    setLimit,
    isLoadingParticipants,
    label_ingresso_inteiro,
    label_ingresso_meia,
    label_ingresso_estudante,
    label_ingresso_crianca,
  } = useVisitor();

  const [selectedEvent, setSelectedEvent] = React.useState<any>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleView = (participant: Participant) => {
    navigate(`/visitors/${participant.id}`);
  };

  const handleExportPDF = async () => {
    if (!eventId) {
      showToast('Selecione um evento para exportar', 'warning');
      return;
    }

    try {
      setIsExporting(true);
      
      // Buscar todos os participantes do evento sem paginação
      const allParticipantsResponse = await visitorService.listParticipantsByEvent(eventId, 1, 10000);
      
      if (!allParticipantsResponse.participants || allParticipantsResponse.participants.length === 0) {
        showToast('Nenhum participante encontrado para exportar', 'warning');
        return;
      }

      const eventData = {
        name: allParticipantsResponse.eventTitle || eventTitle || 'Evento',
        date: new Date().toLocaleDateString(),
        labelInteiro: allParticipantsResponse.label_ingresso_inteiro || label_ingresso_inteiro,
        labelMeia: allParticipantsResponse.label_ingresso_meia || label_ingresso_meia,
        labelEstudante: allParticipantsResponse.label_ingresso_estudante || label_ingresso_estudante,
        labelCrianca: allParticipantsResponse.label_ingresso_crianca || label_ingresso_crianca,
      };

      // Filtrar desistentes do export
      const participantsToExport = allParticipantsResponse.participants.filter(
        (p: any) => p.payment_status !== 'desistente'
      );

      if (participantsToExport.length === 0) {
        showToast('Nenhum participante válido para exportar (todos são desistentes)', 'warning');
        return;
      }

      const result = exportToPDF(participantsToExport, eventData);
      
      if (result.success) {
        showToast(result.message, 'success');
      } else {
        showToast(result.message, 'error');
      }
    } catch (error) {
      showToast('Erro ao exportar PDF', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!eventId) {
      showToast('Selecione um evento para exportar', 'warning');
      return;
    }

    try {
      setIsExporting(true);
      
      // Buscar todos os participantes do evento sem paginação
      const allParticipantsResponse = await visitorService.listParticipantsByEvent(eventId, 1, 10000);
      
      if (!allParticipantsResponse.participants || allParticipantsResponse.participants.length === 0) {
        showToast('Nenhum participante encontrado para exportar', 'warning');
        return;
      }

      const eventData = {
        name: allParticipantsResponse.eventTitle || eventTitle || 'Evento',
        date: new Date().toLocaleDateString(),
        labelInteiro: allParticipantsResponse.label_ingresso_inteiro || label_ingresso_inteiro,
        labelMeia: allParticipantsResponse.label_ingresso_meia || label_ingresso_meia,
        labelEstudante: allParticipantsResponse.label_ingresso_estudante || label_ingresso_estudante,
        labelCrianca: allParticipantsResponse.label_ingresso_crianca || label_ingresso_crianca,
      };

      // Filtrar desistentes do export
      const participantsToExport = allParticipantsResponse.participants.filter(
        (p: any) => p.payment_status !== 'desistente'
      );

      if (participantsToExport.length === 0) {
        showToast('Nenhum participante válido para exportar (todos são desistentes)', 'warning');
        return;
      }

      const result = exportToExcel(participantsToExport, eventData);
      
      if (result.success) {
        showToast(result.message, 'success');
      } else {
        showToast(result.message, 'error');
      }
    } catch (error) {
      showToast('Erro ao exportar Excel', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: '100vw', 
        minHeight: '100vh',
        background: theme.palette.background.default, 
        p: 3,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ 
        maxWidth: '100%',
        width: '100%',
        overflow: 'hidden', 
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            mb: 3,
          }}
        >
          <PageHeader />
          
          {/* Botões de exportação */}
          <Stack direction="row" spacing={2}>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={handleExportExcel} 
              disabled={!eventId || isExporting}
              sx={{
                width: { xs: '140px', md: '140px' },
                minWidth: '140px',
                flexShrink: 0, 
              }}
            >
              {isExporting ? 'Exportando...' : 'Exportar Excel'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleExportPDF} 
              disabled={!eventId || isExporting}
              sx={{
                width: { xs: '140px', md: '140px' },
                minWidth: '140px',
                flexShrink: 0, 
              }}
            >
              {isExporting ? 'Exportando...' : 'Exportar PDF'}
            </Button>
          </Stack>
        </Box>
        
       
        <Box sx={{ 
          mb: 3,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}>
          <EventSelector
            eventId={eventId}
            setEventId={setEventId}
            onSelectEvent={setSelectedEvent}
          />
        </Box>
        
    
        <Box sx={{ 
          width: '100%',
          overflow: 'auto',
        }}>
          <VisitorTable
            participants={participants}
            isLoading={isLoadingParticipants}
            onView={handleView}
            page={page}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            eventId={eventId} 
            labelInteiro={label_ingresso_inteiro}
            labelMeia={label_ingresso_meia}
            labelEstudante={label_ingresso_estudante}
            labelCrianca={label_ingresso_crianca}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function VisitorListPage() {
  const [eventId, setEventId] = useState('');
  return (
    <VisitorProvider eventId={eventId}>
      <VisitorListContent eventId={eventId} setEventId={setEventId} />
    </VisitorProvider>
  );
}